/**
 * Booking Delivery Form
 * Editable: Address fields, delivery price, notes
 * Read-only: Delivery area, distance, time
 */

import { MapPin, Navigation, Clock, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, getCurrencySymbol } from '../lib/currency';
import { mapsHref } from '../utils/maps';
import type { Database } from '../lib/database.types';

type Booking = Database['public']['Tables']['bookings']['Row'];

interface BookingDeliveryFormProps {
  booking: Booking;
  onChange: (updates: Partial<Booking>) => void;
  disabled: boolean;
}

export default function BookingDeliveryForm({ booking, onChange, disabled }: BookingDeliveryFormProps) {
  const { t } = useTranslation();
  const formatPrice = (price: number | null) => {
    return formatCurrency(price, booking.currency);
  };

  return (
    <div className="space-y-lg">
      {/* Delivery Area Info (Read-Only) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-lg p-md bg-tertiary rounded-lg">
        <div>
          <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingDelivery.deliveryArea')}</label>
          <div className="text-primary">{booking.delivery_area_name || t('common.na')}</div>
          {booking.delivery_area_id && (
            <div className="text-xs text-secondary mt-xs">ID: {booking.delivery_area_id}</div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary mb-xs">
            <Navigation className="w-4 h-4 inline mr-xs" />
            {t('bookingDelivery.distance')}
          </label>
          <div className="text-primary">
            {booking.delivery_distance_meters
              ? `${(booking.delivery_distance_meters / 1000).toFixed(2)} km`
              : t('common.na')}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary mb-xs">
            <Clock className="w-4 h-4 inline mr-xs" />
            {t('bookingDelivery.deliveryTime')}
          </label>
          <div className="text-primary">
            {booking.delivery_time_minutes
              ? `${booking.delivery_time_minutes} ${t('bookingDelivery.minutes')}`
              : t('common.na')}
          </div>
        </div>
      </div>

      {/* Delivery Address */}
      <div className="border-t border-primary pt-lg">
        <div className="flex items-center justify-between gap-md mb-md">
          <h3 className="text-lg font-semibold text-primary flex items-center gap-xs">
            <MapPin className="w-5 h-5 text-blue" />
            {t('bookingDelivery.deliveryAddress')}
          </h3>
          {(() => {
            const href = mapsHref({
              street: booking.delivery_street,
              zip: booking.delivery_zip,
              city: booking.delivery_city,
              country: booking.delivery_country,
            });
            const label = t('bookingDelivery.openInMaps');
            const baseClasses =
              'inline-flex items-center gap-xs px-md py-xs text-sm font-medium rounded-lg border border-primary/20 bg-tertiary text-primary transition-colors';
            if (href) {
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className={`${baseClasses} hover:bg-blue/10 hover:text-blue hover:border-blue/30`}
                >
                  <Navigation className="w-4 h-4" />
                  <span className="hidden sm:inline">{label}</span>
                </a>
              );
            }
            return (
              <button
                type="button"
                disabled
                aria-label={label}
                title={label}
                className={`${baseClasses} opacity-50 cursor-not-allowed`}
              >
                <Navigation className="w-4 h-4" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            );
          })()}
        </div>
        <div className="grid grid-cols-1 gap-lg">
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('bookingDelivery.streetAddress')}
            </label>
            <input
              type="text"
              value={booking.delivery_street || ''}
              onChange={(e) => onChange({ delivery_street: e.target.value })}
              disabled={disabled}
              className="input w-full"
              placeholder={t('bookingDelivery.streetAddressPlaceholder')}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('bookingDelivery.zipCode')}
              </label>
              <input
                type="text"
                value={booking.delivery_zip || ''}
                onChange={(e) => onChange({ delivery_zip: e.target.value })}
                disabled={disabled}
                className="input w-full"
                placeholder="12345"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('bookingDelivery.city')}
              </label>
              <input
                type="text"
                value={booking.delivery_city || ''}
                onChange={(e) => onChange({ delivery_city: e.target.value })}
                disabled={disabled}
                className="input w-full"
                placeholder={t('bookingDelivery.cityPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('bookingDelivery.country')}
              </label>
              <input
                type="text"
                value={booking.delivery_country || ''}
                onChange={(e) => onChange({ delivery_country: e.target.value })}
                disabled={disabled}
                className="input w-full"
                placeholder={t('bookingDelivery.countryPlaceholder')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Price */}
      <div className="border-t border-primary pt-lg">
        <h3 className="text-lg font-semibold text-primary mb-md flex items-center gap-xs">
          <DollarSign className="w-5 h-5 text-blue" />
          {t('bookingDelivery.deliveryPricing')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('bookingDelivery.deliveryPrice')}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary pointer-events-none">{getCurrencySymbol(booking.currency)}</span>
              <input
                type="number"
                value={booking.delivery_price || 0}
                onChange={(e) => onChange({ delivery_price: parseFloat(e.target.value) || 0 })}
                disabled={disabled}
                className="input w-full"
                style={{ paddingLeft: '2rem' }}
                min="0"
                step="0.01"
              />
            </div>
            <p className="text-xs text-secondary mt-xs">
              {t('bookingDelivery.deliveryPriceHint')}
            </p>
          </div>
          <div className="flex items-end">
            <div className="p-md bg-tertiary rounded-lg w-full">
              <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingDelivery.currentValue')}</label>
              <div className="text-2xl font-bold text-primary">{formatPrice(booking.delivery_price)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Notes */}
      <div className="border-t border-primary pt-lg">
        <h3 className="text-lg font-semibold text-primary mb-md">
          {t('bookingDelivery.deliveryNotes')}
        </h3>
        <div>
          <label className="block text-sm font-medium text-primary mb-xs">
            {t('bookingDelivery.specialInstructions')}
          </label>
          <textarea
            value={booking.delivery_notes || ''}
            onChange={(e) => onChange({ delivery_notes: e.target.value })}
            disabled={disabled}
            className="input w-full"
            rows={4}
            placeholder={t('bookingDelivery.specialInstructionsPlaceholder')}
          />
        </div>
      </div>
    </div>
  );
}
